// Dossier application préparé par le cockpit (programmes en mode externe).
//
// Anissa a déjà validé le programme : le cockpit propose lui-même les lots
// Introduction et stratégie (lot 1) et Menus et recettes (lot 2), pré-remplit
// les lots 3 et 4, et elle relit. Rien n'est appliqué ni validé ici :
//   - chaque lot généré passe par EXACTEMENT les mêmes contrôles que le lot
//     collé depuis ChatGPT (parseClientAppWorkspaceLot / importClientAppWorkspaceLot) ;
//   - les menus générés arrivent dans « Semaine, menus & recettes » comme
//     modifications enregistrées À VALIDER, repas par repas, par Anissa ;
//   - fail-closed : profil à risque non confirmé, TCA (et autres profils à
//     validation obligatoire), grossesse/allaitement/post-partum, mineure →
//     aucune génération, message clair. Le chemin manuel reste disponible.
//
// Aucune identité n'est envoyée à l'IA : le programme est anonymisé par
// buildClientAppWorkspaceContext (redactDirectIdentifiers), les profils
// alimentaires sont filtrés et anonymisés par les générateurs existants, et
// toute sortie qui porte encore une mention anonymisée est refusée.

import { callClaude, ClaudeApiError, safeParseJson } from "./anthropic";
import { sha256HexSync } from "./sha256Sync";
import { ANISSA_IDENTITY_CORE } from "./prompts/nutrition/identity.fr";
import { buildSafetyBlockFr } from "./prompts/nutrition/_clinicalContext.fr";
import { resolveMaternalStateExtended } from "./prompts/nutrition/profiles/_detector.fr";
import {
  findUnknownRiskFlags,
  hasRiskProfileAssessment,
  resolveMandatoryRiskFlags,
  riskFlagLabelFr,
} from "./riskProfileFlags";
import { classifyAge, findExclusionTermsPresent } from "./clinicalClearance";
import { analyzeFoodExclusions, findMenuFoodConflicts } from "./explicitFoodGuard";
import { normalizeForMatch } from "./clinicalInteractions";
import { assertNoAiMention, NO_SELF_MENTION_INSTRUCTION } from "./aiMentionGuard";
import { isExternalPlanInput, redactDirectIdentifiers } from "./externalPlanWorkflow";
import {
  buildRecipeProfileContextLines,
  generateRecipesForMeals,
  sanitizeRecipeForm,
} from "./aiRecipeGenerator";
import { generateAlternativesForMeals } from "./aiAlternativesGenerator";
import { mealKey } from "./extractMealsFromPlan";
import { formatStructuredIngredient } from "./structuredRecipe";
import { addAlternative, buildWeekMenusModel, markWeekMenusSaved } from "./weekMenus";
import { etatPropositionsMgd } from "../suivi/services/suiviPropositions";
import { normalizeNutritionConsultationCloudRow } from "./nutritionConsultationPersistence";
import {
  MEALS_AUTHORITY_SOURCES,
  buildClientAppWorkspaceContext,
  buildClientAppWorkspaceLotTemplate,
  weekEditorContentSha256,
  clientAppWorkspaceLotFormatRules,
  hasWeekEditorMeals,
  importClientAppWorkspaceLot,
  markClientAppWorkspaceWeekEditorSave,
  parseClientAppWorkspaceLot,
} from "./clientAppWorkspace";

export const AUTO_LOT_IDS = Object.freeze(["lot_1", "lot_2"]);
export const LOT_3_DEFAULTS = Object.freeze({
  people: 1,
  session_count: 2,
  preferred_days: Object.freeze(["sunday", "wednesday"]),
});
const LOT_2_ALTERNATIVE_COUNT = 4;
const ANONYMIZED_MARK_RE = /\[(?:identité|coordonnée|lien|date|identifiant|lieu|adresse|nom de tiers) retiré(?:e)?\]/i;
export const MESSAGE_AUTO_MGD = "Programme MGD : la préparation automatique n’est pas disponible. Fais le dossier à la main.";

/** 'ouvert' | 'mgd' | 'illisible' — même lecture que les propositions du suivi. */
export function readDossierAutoMgdState(clientId, options) {
  return etatPropositionsMgd(clientId, options);
}

const MATERNAL_FIELDS = ["grossesseActuelle", "allaitement", "postPartum"];

function externalInputOf(consultation) {
  return consultation?.editorial_overrides?.external_plan_input
    ?? consultation?.editorialOverrides?.external_plan_input;
}

/** Programme préparé à partir de la synthèse du dossier (mode externe). */
export function isExternalDossierConsultation(consultation) {
  return isExternalPlanInput(externalInputOf(consultation));
}

function identityOf(client) {
  return {
    prenom: client?.prenom,
    nom: client?.nom || client?.form?.nom,
    email: client?.email || client?.form?.email,
  };
}

/**
 * Fiche transmise aux générateurs existants : identité ajoutée UNIQUEMENT
 * pour qu'ils puissent la retirer (ils anonymisent avec la fiche elle-même).
 * Aucun de leurs blocs ne cite ces champs.
 */
function generatorForm(client) {
  const form = client?.form && typeof client.form === "object" ? client.form : {};
  const identity = identityOf(client);
  return {
    ...form,
    ...(identity.prenom ? { prenom: identity.prenom } : {}),
    ...(identity.nom ? { nom: identity.nom } : {}),
    ...(identity.email ? { email: identity.email } : {}),
  };
}

/**
 * Ce qui empêche le cockpit de préparer le dossier lui-même. `null` = rien.
 * Portes des générateurs existants, en plus strict (aucun override ici) :
 * profil confirmé, aucun profil à validation obligatoire (TCA, cancer actif,
 * insuffisance hépatique), aucun flag inconnu, majeure d'âge connu, statut
 * maternel confirmé et négatif, pas de MGD, pas de présentation sans chiffres.
 * Un profil à risque SANS validation obligatoire (chirurgie récente) ne
 * bloque pas : ses contraintes partent dans le bloc sécurité, comme pour les
 * générateurs existants (à valider par Anissa, voir docs/DOSSIER-APP-AUTO.md).
 */
export function getDossierAutoGenerationBlocker({ client, consultation, mgdState }) {
  // Tous les programmes : mode externe (synthèse du dossier) ou plan généré
  // dans le cockpit. Seule une consultation absente empêche la préparation.
  if (!consultation?.id) return "Programme indisponible : recharge la fiche.";
  // MGD (à valider Anissa) : rapport actif OU statut illisible / pas encore
  // lu = fermé, même règle que les propositions du suivi (etatPropositionsMgd).
  if (mgdState !== "ouvert") return MESSAGE_AUTO_MGD;
  const form = client?.form;
  if (!hasRiskProfileAssessment(form) || buildRecipeProfileContextLines(form).length === 0) {
    return "Profil et allergies à compléter puis confirmer dans Profil avant la préparation automatique.";
  }
  const unknown = findUnknownRiskFlags(form);
  if (unknown.length > 0) {
    return `Profil à risque non reconnu (« ${unknown.join(" », « ")} ») : vérifie la fiche. Aucune préparation automatique.`;
  }
  const mandatory = resolveMandatoryRiskFlags(form);
  if (mandatory.length > 0) {
    return `Profil à risque déclaré — ${mandatory.map(riskFlagLabelFr).join(", ")} : la préparation automatique est désactivée. Prépare ce dossier toi-même.`;
  }
  // Présentation sans chiffres (à valider Anissa) : recettes et Meal Prep
  // sont chiffrés ; tant qu'elle n'a pas tranché, pas de préparation auto.
  if (form?.presentationSansChiffres === true) {
    return "Présentation sans chiffres : la préparation automatique n’est pas disponible, les recettes et le Meal Prep sont chiffrés. Fais le dossier à la main.";
  }
  const ageClass = classifyAge(form);
  if (ageClass === "minor") {
    return "Cliente mineure : la préparation automatique est désactivée. Prépare ce dossier toi-même.";
  }
  // Seconde barrière : un âge inconnu ne vaut pas majorité.
  if (ageClass !== "adult") {
    return "Âge non renseigné ou invalide : complète-le dans l’anamnèse avant la préparation automatique.";
  }
  const unanswered = MATERNAL_FIELDS.filter((field) => !["oui", "non"].includes(String(form?.[field] ?? "").trim().toLowerCase()));
  if (unanswered.length > 0) {
    return "Grossesse, allaitement et post-partum doivent être confirmés par Oui ou Non dans l’anamnèse avant la préparation automatique.";
  }
  if (resolveMaternalStateExtended(form)) {
    return "Grossesse, allaitement ou post-partum déclaré : la préparation automatique est désactivée. Prépare ce dossier toi-même.";
  }
  return null;
}

export const MESSAGE_LOT_2_COMPLETE = "Menus déjà complets : rien à générer. Relis-les dans « Semaine, menus & recettes ».";

export const MESSAGE_LOT_2_CHOSEN_EXPLICITLY = "Tu as choisi le lot 2 comme source des menus : la préparation automatique ne les modifie pas.";

/** Toute recette enregistrée compte, même réduite à une consigne : jamais remplacée. */
function recipePresent(recipes, key) {
  return Object.hasOwn(recipes || {}, key) && recipes[key] != null;
}

function incompleteRecipesMessage(count) {
  return `${count} recette${count > 1 ? "s" : ""} incomplète${count > 1 ? "s" : ""} à finir dans « Semaine, menus & recettes » (ingrédients et étapes).`;
}

/**
 * Que faire pour les menus ?
 * - « full » : aucun menu ni recette existant → lot 2 complet, comme avant ;
 * - « complete » : des menus ou des recettes existent (cas courant : recettes
 *   générées dans l'éditeur du plan) → rien n'est remplacé, seul ce qui
 *   manque est proposé : alternatives jusqu'à 4 par repas, recettes des
 *   choix qui n'en ont pas. Les repas déjà complets ne sont pas touchés.
 */
export function planLot2Completion({ client, consultation, workspace = null, context }) {
  // Choix explicite d'Anissa : jamais renversé par le cockpit.
  if (workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.LOT_2 && workspace.meals_authority.chosen_explicitly === true) {
    return { mode: "complete", slots: [], skipped: [], complete: false, incompleteRecipes: 0, error: MESSAGE_LOT_2_CHOSEN_EXPLICITLY };
  }
  const overrides = consultation?.editorial_overrides || {};
  const recipes = consultation?.meal_recipes && typeof consultation.meal_recipes === "object" ? consultation.meal_recipes : {};
  const fresh = !(Array.isArray(overrides.meals) && overrides.meals.length > 0)
    && !hasWeekEditorMeals(consultation)
    && workspace?.meals_authority?.source !== MEALS_AUTHORITY_SOURCES.WEEK_EDITOR
    && !dossierPrimaryRecipeKeys(context).some((key) => Object.hasOwn(recipes, key));
  if (fresh) return { mode: "full", slots: [], skipped: [], complete: false, incompleteRecipes: 0, error: null };
  let model;
  try {
    model = buildWeekMenusModel({ client, consultation });
  } catch {
    model = null;
  }
  if (!model || model.slots.length === 0) {
    return { mode: "complete", slots: [], skipped: [], complete: false, incompleteRecipes: 0, error: "La semaine des menus est illisible : complète-la dans « Semaine, menus & recettes »." };
  }
  const slots = [];
  const skipped = [];
  let incompleteRecipes = 0;
  for (const slotModel of model.slots) {
    if (slotModel.variesByDay) {
      skipped.push(`${slotModel.label} : le repas varie selon les jours, complète-le toi-même dans « Semaine, menus & recettes ».`);
      continue;
    }
    const alternatives = slotModel.choices.filter((choice) => choice.kind === "alternative");
    const missingAlternatives = Math.max(0, LOT_2_ALTERNATIVE_COUNT - alternatives.length);
    const missingRecipes = slotModel.choices
      .filter((choice) => !recipePresent(recipes, choice.recipeKey))
      .map((choice) => ({
        key: choice.recipeKey,
        slot: slotModel.slot,
        slot_label: slotModel.label,
        title: choice.title,
        ...(choice.hint ? { hint: choice.hint } : {}),
      }));
    // Recette présente mais incomplète : elle est à Anissa, jamais complétée ici.
    incompleteRecipes += slotModel.choices.filter((choice) => recipePresent(recipes, choice.recipeKey) && !choice.recipeComplete).length;
    if (missingAlternatives > 0 || missingRecipes.length > 0) {
      slots.push({
        slot: slotModel.slot,
        label: slotModel.label,
        primaryTitle: slotModel.choices[0].title,
        existingTitles: slotModel.choices.map((choice) => choice.title),
        // Clés déjà prises par une recette (même d'un ancien titre) : une
        // nouvelle alternative ne s'y rattache jamais.
        takenKeys: Object.keys(recipes).filter((key) => key.startsWith(`${slotModel.slot}::`)),
        missingAlternatives,
        missingRecipes,
      });
    }
  }
  return { mode: "complete", slots, skipped, complete: slots.length === 0, incompleteRecipes, error: null };
}

/** Rien à faire pour les menus (déjà complets, éditeur déjà source), ou raison de ne pas les toucher. */
export function getLot2AutoGenerationBlocker({ client, consultation, workspace = null, context }) {
  const plan = planLot2Completion({ client, consultation, workspace, context });
  if (plan.error) return plan.error;
  if (plan.complete) {
    // « Complets » seulement si chaque recette a ses ingrédients ET ses étapes.
    if (plan.incompleteRecipes > 0) return incompleteRecipesMessage(plan.incompleteRecipes);
    if (workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR) return MESSAGE_LOT_2_COMPLETE;
    if (plan.skipped.length > 0) return plan.skipped.join(" ");
  }
  return null;
}

export function dossierPrimaryRecipeKeys(context) {
  return (context?.packageContext?.meals || []).map((meal) => mealKey(meal.slot, meal.title));
}

function assertNoAnonymizedMark(value, lotLabel) {
  if (ANONYMIZED_MARK_RE.test(JSON.stringify(value ?? null))) {
    throw new Error(`${lotLabel} : la proposition contient une mention anonymisée. Relance ce lot.`);
  }
}

const SUPPLEMENT_WORD_RE = /(?<![\p{L}\p{N}])(compl[ée]ments?|suppl[ée]ments?|suppl[ée]mentation|g[ée]lules?|capsules?|comprim[ée]s?|posologies?|dosages?|cures?|ampoules?|gouttes?|sachets?|pastilles?)(?![\p{L}\p{N}])/iu;
const DOSE_RE = /\d+(?:[.,]\d+)?\s*(?:mg|µg|μg|mcg|ug|ui|iu)(?![\p{L}\p{N}])/iu;

function textOf(value) {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(textOf);
  if (value && typeof value === "object") return Object.values(value).flatMap(textOf);
  return [];
}

/**
 * Lot 1 rédigé par le cockpit : une dose chiffrée bloque. Un aliment exclu
 * ou une mention de complément est signalé à Anissa, sans bloquer.
 * @returns {string[]} points à vérifier par Anissa (non bloquants)
 */
export function assertLot1TextSafe(content, client, context) {
  const text = textOf(content).join("\n");
  const form = client?.form || {};
  const { exclusions, uninterpretable } = analyzeFoodExclusions({
    allergies: form.allergies,
    alimentsEvites: form.alimentsEvites,
    restrictionsAutre: form.restrictionsAutre,
  });
  // Décision de Benoit (17.10) : signaler sans bloquer. L'introduction et la
  // stratégie expliquent le programme et nomment souvent l'exclusion elle-même
  // (« alimentation pauvre en nickel ») ; Anissa relit ces deux sections avant
  // de les cocher, et la publication garde son contrôle clinique.
  const excluded = findExclusionTermsPresent(text, exclusions);
  const excludedWarnings = excluded.length > 0
    ? [`Le texte cite ${excluded.map((term) => `« ${term} »`).join(", ")}, présent dans les exclusions de la cliente : vérifie que l’introduction et la stratégie en parlent seulement pour l’éviter ou le limiter.`]
    : [];
  // Noms du protocole : mots entiers, normalisés comme les exclusions
  // (« Fer » ne bloque pas « préférer », « Vitamine D » pas « vitamine du »).
  const names = (context?.supplementItems || [])
    .map((item) => normalizeForMatch(String(item?.name || "")).replace(/œ/g, "oe").replace(/[’'ʼ`´]/g, " ").replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const [supplementName] = findExclusionTermsPresent(text, names);
  // « en complément (de ton repas) » est une tournure, pas un complément.
  const supplementWord = [...text.matchAll(new RegExp(SUPPLEMENT_WORD_RE.source, "giu"))]
    .find((found) => !(/^compl[ée]ments?$/i.test(found[0]) && /(?<![\p{L}\p{N}])en\s+$/iu.test(text.slice(0, found.index))))?.[0];
  // Décision de Benoit (17.10, cas Guillemette) : seule une dose chiffrée
  // (« 300 mg », « 1000 UI ») bloque. Un mot de complément (« en cure »,
  // « dosage à valider ») ou un nom du protocole est signalé : le programme
  // validé par Anissa en parle, l'introduction le reformule, et Anissa relit
  // les deux sections avant de les cocher.
  const dose = text.match(DOSE_RE)?.[0];
  if (dose) {
    throw new Error(`Introduction et stratégie : le texte donne une dose chiffrée (« ${dose} »). Les doses restent dans la section Compléments : relance ce lot ou rédige-le toi-même.`);
  }
  const mention = supplementWord || supplementName;
  const supplementWarnings = mention
    ? [`Le texte parle de complément (« ${mention} ») : vérifie que l’introduction et la stratégie ne donnent ni dose ni consigne nouvelle, les compléments restent présentés dans leur section.`]
    : [];
  // Exclusions écrites en prose : jamais devinées, signalées à Anissa
  // (sans bloquer, comme pour les menus).
  return [
    ...excludedWarnings,
    ...supplementWarnings,
    ...uninterpretable.map((fragment) => `Exclusion non interprétable « ${fragment} » : vérifie toi-même l’introduction et la stratégie.`),
  ];
}

function envelopeText(context, workspace, lotId, content) {
  return JSON.stringify({ ...buildClientAppWorkspaceLotTemplate(context, lotId, workspace), content });
}

// ─── Lot 1 ──────────────────────────────────────────────────────────────

const LOT_1_BODY = `DOSSIER APPLICATION — INTRODUCTION ET STRATÉGIE

Tu rédiges, pour l'application de la cliente, le message d'accueil et la
stratégie de SON programme, déjà validé par Anissa. Le programme fourni est
la seule source : tu le reformules pour la cliente, tu n'y ajoutes rien.

INTERDIT, sans exception : ajouter ou modifier un complément, un dosage, une
posologie, un diagnostic, une durée de cure, un résultat d'analyse, une
promesse ou toute affirmation de santé absente du programme. Introduire un
aliment exclu. Contredire une décision du programme.

TON : tutoiement, voix d'Anissa à la première personne, phrases courtes,
pas de jargon, pas de marketing, pas de tiret cadratin, pas de règle de trois.

COMPLÉMENTS : ne cite aucun complément, aucune dose, aucune gélule ni
aucune cure. Ils sont présentés tels quels dans leur propre section.

OUTIL : ${NO_SELF_MENTION_INSTRUCTION}

IDENTITÉ : le programme est anonymisé. N'écris aucun prénom ni nom. La
salutation reste générale, par exemple « Bonjour, ». La signature est
exactement « Anissa ».

RÉPONSE : uniquement l'objet JSON suivant, sans markdown ni commentaire :
{
  "introduction": { "greeting": "", "body": [""], "pull_quote": "", "tailored_points": [{ "title": "", "detail": "" }], "signature": "Anissa" },
  "strategy": { "subtitle": "", "essential": [""], "pillars": [{ "title": "", "description": "" }], "takeaways": [""] }
}`;

export function buildLot1AutoPrompts({ client, context }) {
  const safetyForm = sanitizeRecipeForm(generatorForm(client));
  const system = [
    ANISSA_IDENTITY_CORE,
    buildSafetyBlockFr(safetyForm),
    LOT_1_BODY,
    clientAppWorkspaceLotFormatRules("lot_1"),
  ].filter(Boolean).join("\n\n");
  const directives = context.packageContext?.directives || "";
  const user = [
    "=== PROGRAMME VALIDÉ PAR ANISSA (anonymisé) ===",
    context.narrativeSource,
    "=== FIN DU PROGRAMME ===",
    directives ? `=== DIRECTIVES D'ANISSA ===\n${directives}` : "",
    "Rédige l'introduction et la stratégie en JSON strict.",
  ].filter(Boolean).join("\n\n");
  return { system, user };
}

/**
 * Propose le lot 1 et le vérifie avec le contrôle du lot collé.
 * @returns {Promise<{ rawText: string, warnings: string[] }>} lot complet (enveloppe JSON) et points à vérifier
 */
export async function generateClientAppLot1({ client, context, workspace = null, signal = null }) {
  const { system, user } = buildLot1AutoPrompts({ client, context });
  const raw = await callClaude({ system, user, maxTokens: 10000, signal });
  const parsed = safeParseJson(raw);
  const content = { introduction: parsed?.introduction, strategy: parsed?.strategy };
  assertNoAnonymizedMark(content, "Introduction et stratégie");
  const warnings = assertLot1TextSafe(content, client, context);
  const rawText = envelopeText(context, workspace, "lot_1", content);
  parseClientAppWorkspaceLot(rawText, context, "lot_1");
  return { rawText, warnings };
}

// ─── Lot 2 ──────────────────────────────────────────────────────────────

function normalizedTitle(value) {
  return String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("fr").replace(/\s+/g, " ").trim();
}

function packageRecipe(recipe, label) {
  if (!recipe || !Array.isArray(recipe.ingredients_structured) || !Array.isArray(recipe.preparation)) {
    throw new Error(`${label} : recette manquante. Relance le lot Menus et recettes.`);
  }
  const tip = typeof recipe.tip === "string" ? recipe.tip.replace(/\s+/g, " ").trim().slice(0, 500) : "";
  return {
    ingredients_structured: recipe.ingredients_structured,
    preparation: recipe.preparation,
    prep_time_min: Number.isInteger(recipe.prep_time_min) ? recipe.prep_time_min : 0,
    cook_time_min: Number.isInteger(recipe.cook_time_min) ? recipe.cook_time_min : 0,
    servings: Number.isInteger(recipe.servings) ? recipe.servings : 1,
    ...(tip ? { tip } : {}),
  };
}

function assertNoDirectIdentifier(text, identity, label) {
  if (!text) return;
  if (redactDirectIdentifiers(text, identity, { maxLength: 300 }) !== String(text).replace(/\s+/g, " ").trim()) {
    throw new Error(`${label} contient une donnée identifiante. Corrige-le dans le programme ou relance ce lot.`);
  }
}

function cleanHint(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim().slice(0, 300) : "";
}

/**
 * Propose le lot 2 (menus, 4 alternatives, recettes chiffrées) avec les
 * générateurs existants (même bloc sécurité, même fail-closed), puis le
 * vérifie avec le contrôle du lot collé (structure, grammages, aliments exclus).
 * @returns {Promise<{ rawText: string, content: object, recipes: object }>}
 */
const AI_CONCURRENCY = 3;
const ALTERNATIVES_CHUNK = 4;
const RECIPES_CHUNK = 5;
// Lots IA réussis gardés en mémoire quand un AUTRE lot a échoué (délai,
// saturation) : « Réessayer » ne refait que ce qui manque. Vidé dès que le
// lot 2 est complet ou refusé par un contrôle (rien de refusé n'est réutilisé).
const lot2BatchCache = new Map();

function abortedError() {
  return new ClaudeApiError("Génération annulée : interrompue", 0, { aborted: true, reason: "external" });
}

/** File d'attente : au plus `max` appels à l'IA en même temps. */
function createLimiter(max, signal) {
  let active = 0;
  const queue = [];
  const pump = () => {
    while (active < max && queue.length > 0) {
      const { task, resolve, reject } = queue.shift();
      if (signal?.aborted) {
        reject(abortedError());
        continue;
      }
      active += 1;
      Promise.resolve().then(task).then(resolve, reject).finally(() => {
        active -= 1;
        pump();
      });
    }
  };
  return (task) => new Promise((resolve, reject) => {
    queue.push({ task, resolve, reject });
    pump();
  });
}

function chunks(items, size) {
  const out = [];
  for (let index = 0; index < items.length; index += size) out.push(items.slice(index, index + size));
  return out;
}

function lot2CacheKey(client, context) {
  return sha256HexSync(stableJson({
    workspace: context.sourceRevision,
    package: context.packageContext?.sourceRevision,
    form: client?.form ?? null,
  }));
}

function clearLot2Cache(prefix) {
  for (const key of [...lot2BatchCache.keys()]) {
    if (key.startsWith(prefix)) lot2BatchCache.delete(key);
  }
}

/** Lance les lots manquants ; garde les réussis ; renvoie la première erreur. */
async function runCachedBatches({ items, size, cacheKeyOf, run, limit }) {
  const missing = items.filter((item) => !lot2BatchCache.has(cacheKeyOf(item)));
  const settled = await Promise.allSettled(chunks(missing, size).map((chunk) => limit(async () => {
    const value = await run(chunk);
    for (const item of chunk) lot2BatchCache.set(cacheKeyOf(item), value(item));
  })));
  const failure = settled.find((entry) => entry.status === "rejected");
  if (failure) throw failure.reason;
  return new Map(items.map((item) => [item.key, lot2BatchCache.get(cacheKeyOf(item))]));
}

export function _resetDossierAutoCacheForTests() {
  lot2BatchCache.clear();
}

export async function generateClientAppLot2({ client, context, workspace = null, signal = null, limit = createLimiter(AI_CONCURRENCY, signal) }) {
  const prefix = `${lot2CacheKey(client, context)}|`;
  try {
    const result = await generateClientAppLot2Uncached({ client, context, workspace, limit, prefix });
    clearLot2Cache(prefix);
    return result;
  } catch (error) {
    // Seuls les échecs d'appel (délai, saturation, réseau) gardent les lots
    // réussis ; un contrôle refusé repart de zéro.
    const callFailure = error instanceof ClaudeApiError && !error.payload?.parseError;
    if (!callFailure) clearLot2Cache(prefix);
    throw error;
  }
}

async function generateClientAppLot2Uncached({ client, context, workspace, limit, prefix }) {
  const form = generatorForm(client);
  const identity = identityOf(client);
  const directives = context.packageContext?.directives || "";
  const meals = context.packageContext.meals.map((meal) => {
    // Titre et description ne partent à l'IA que s'ils ne portent aucune
    // donnée identifiante (sinon la clé de recette ne correspondrait plus).
    assertNoDirectIdentifier(meal.title, identity, `${meal.slot_label} : le titre du repas`);
    assertNoDirectIdentifier(meal.hint, identity, `${meal.slot_label} : la description du repas`);
    return { key: mealKey(meal.slot, meal.title), slot: meal.slot, slot_label: meal.slot_label, title: meal.title, ...(meal.hint ? { hint: meal.hint } : {}) };
  });
  const proposalsByKey = await runCachedBatches({
    items: meals,
    size: ALTERNATIVES_CHUNK,
    cacheKeyOf: (meal) => `${prefix}alt|${meal.key}`,
    limit,
    run: async (chunk) => {
      const proposals = await generateAlternativesForMeals(chunk, { form, directives });
      return (meal) => proposals.find((entry) => entry.slot === meal.slot) || null;
    },
  });
  const choicesBySlot = new Map();
  for (const meal of meals) {
    const proposal = proposalsByKey.get(meal.key);
    const seen = new Set([normalizedTitle(meal.title)]);
    const alternatives = [];
    for (const alternative of proposal?.alternatives || []) {
      const title = String(alternative?.title || "").replace(/\s+/g, " ").trim().slice(0, 180);
      if (title.length < 3 || seen.has(normalizedTitle(title))) continue;
      seen.add(normalizedTitle(title));
      const hint = cleanHint(alternative.hint);
      assertNoDirectIdentifier(title, identity, `${meal.slot_label} : le titre d'une alternative`);
      assertNoDirectIdentifier(hint, identity, `${meal.slot_label} : la description d'une alternative`);
      alternatives.push({ title, hint });
      if (alternatives.length === LOT_2_ALTERNATIVE_COUNT) break;
    }
    if (alternatives.length < LOT_2_ALTERNATIVE_COUNT) {
      throw new Error(`${meal.slot_label} : moins de ${LOT_2_ALTERNATIVE_COUNT} alternatives différentes proposées. Relance le lot Menus et recettes.`);
    }
    choicesBySlot.set(meal.slot, alternatives);
  }
  const recipeMeals = meals.flatMap((meal) => [
    meal,
    ...choicesBySlot.get(meal.slot).map((alternative) => ({
      key: mealKey(meal.slot, alternative.title),
      slot: meal.slot,
      slot_label: meal.slot_label,
      title: alternative.title,
      ...(alternative.hint ? { hint: alternative.hint } : {}),
    })),
  ]);
  const recipesByKey = await runCachedBatches({
    items: recipeMeals,
    size: RECIPES_CHUNK,
    cacheKeyOf: (meal) => `${prefix}recipe|${meal.key}`,
    limit,
    run: async (chunk) => {
      const generated = await generateRecipesForMeals(chunk, { form, directives });
      return (meal) => generated[meal.key] || null;
    },
  });
  const recipes = Object.fromEntries([...recipesByKey.entries()].filter(([, recipe]) => recipe));
  const choice = (meal, title, hint) => {
    const key = mealKey(meal.slot, title);
    return {
      title,
      ...(hint ? { hint } : {}),
      recipe: packageRecipe(recipes[key], `${meal.slot_label} · ${title}`),
    };
  };
  const content = {
    meals: meals.map((meal) => ({
      slot: meal.slot,
      primary: choice(meal, meal.title, cleanHint(meal.hint)),
      alternatives: choicesBySlot.get(meal.slot).map((alternative) => choice(meal, alternative.title, alternative.hint)),
    })),
    grammages_provenance: {
      source_revision: context.packageContext.sourceRevision,
      status: "proposed_for_anissa_review",
    },
  };
  assertNoAnonymizedMark(content, "Menus et recettes");
  const rawText = envelopeText(context, workspace, "lot_2", content);
  parseClientAppWorkspaceLot(rawText, context, "lot_2");
  return { rawText, content, recipes };
}

/**
 * Lot 2 importé dans le dossier ET menus posés dans « Semaine, menus &
 * recettes » comme modifications enregistrées, À VALIDER repas par repas.
 * L'éditeur devient la source des menus du dossier : rien n'est validé ici.
 */
export function applyGeneratedLot2({ client, consultation, context, workspace, rawText, content, recipes }) {
  // Lot complet seulement s'il n'existe toujours aucun menu ni recette :
  // sinon rien n'est écrasé (le chemin « compléter » s'applique).
  const existingRecipes = consultation?.meal_recipes || {};
  const targetKeys = content.meals.flatMap((meal) => [meal.primary, ...meal.alternatives].map((option) => mealKey(meal.slot, option.title)));
  if (planLot2Completion({ client, consultation, workspace, context }).mode !== "full"
      || targetKeys.some((key) => Object.hasOwn(existingRecipes, key))) {
    throw new Error("Des menus ou des recettes sont apparus entre-temps : rien n’a été remplacé. Relance la préparation.");
  }
  const importedWorkspace = importClientAppWorkspaceLot({ workspace, context, lotId: "lot_2", rawText });
  const overrides = consultation?.editorial_overrides || {};
  const slots = new Set(content.meals.map((meal) => meal.slot));
  const mealRecipes = { ...(consultation?.meal_recipes || {}) };
  const entries = content.meals.map((meal) => {
    for (const option of [meal.primary, ...meal.alternatives]) {
      const key = mealKey(meal.slot, option.title);
      // Même recette que celle du lot vérifié (nettoyée), avec ses lignes
      // d'ingrédients lues par l'éditeur Semaine.
      mealRecipes[key] = {
        ...option.recipe,
        ingredients: option.recipe.ingredients_structured.map(formatStructuredIngredient),
      };
    }
    return {
      slot: meal.slot,
      title: meal.primary.title,
      hint: meal.primary.hint || "",
      edited_in: "week_editor",
      alternatives: meal.alternatives.map((alternative) => ({ title: alternative.title, hint: alternative.hint || "" })),
    };
  });
  const next = {
    ...consultation,
    meal_recipes: mealRecipes,
    editorial_overrides: {
      ...overrides,
      meals: [
        ...(Array.isArray(overrides.meals) ? overrides.meals : []).filter((entry) => !slots.has(String(entry?.slot || "").trim())),
        ...entries,
      ],
    },
  };
  const saved = markWeekMenusSaved({ client, previous: consultation, next }).consultation;
  return markClientAppWorkspaceWeekEditorSave({
    ...saved,
    editorial_overrides: { ...saved.editorial_overrides, client_app_workspace: importedWorkspace },
  });
}

// ─── Lot 2, menus existants : compléter sans rien remplacer ────────────

function storedRecipe(recipe, label) {
  const clean = packageRecipe(recipe, label);
  return { ...clean, ingredients: clean.ingredients_structured.map(formatStructuredIngredient) };
}

/**
 * Propose uniquement ce qui manque (alternatives, recettes absentes) pour les
 * repas incomplets. Mêmes générateurs, même bloc sécurité ; contrôles
 * d'identité et d'aliments exclus sur le contenu généré seulement.
 * @returns {Promise<{ alternativesBySlot: Record<string, {title: string, hint: string}[]>, recipes: Record<string, object>, completedSlots: number }>}
 */
export async function generateLot2Completion({ client, context, plan, signal = null, limit = createLimiter(AI_CONCURRENCY, signal) }) {
  const prefix = `${lot2CacheKey(client, context)}|complete|`;
  try {
    const result = await generateLot2CompletionUncached({ client, context, plan, limit, prefix });
    clearLot2Cache(prefix);
    return result;
  } catch (error) {
    if (!(error instanceof ClaudeApiError && !error.payload?.parseError)) clearLot2Cache(prefix);
    throw error;
  }
}

async function generateLot2CompletionUncached({ client, context, plan, limit, prefix }) {
  const form = generatorForm(client);
  const identity = identityOf(client);
  const directives = context.packageContext?.directives || "";
  for (const slot of plan.slots) {
    assertNoDirectIdentifier(slot.primaryTitle, identity, `${slot.label} : le titre du repas`);
    for (const item of slot.missingRecipes) {
      assertNoDirectIdentifier(item.title, identity, `${slot.label} : le titre « ${item.title} »`);
      assertNoDirectIdentifier(item.hint, identity, `${slot.label} : la description de « ${item.title} »`);
    }
  }
  const needingAlternatives = plan.slots.filter((slot) => slot.missingAlternatives > 0)
    .map((slot) => ({ key: mealKey(slot.slot, slot.primaryTitle), slot: slot.slot, slot_label: slot.label, title: slot.primaryTitle }));
  const proposalsByKey = needingAlternatives.length > 0
    ? await runCachedBatches({
      items: needingAlternatives,
      size: ALTERNATIVES_CHUNK,
      cacheKeyOf: (meal) => `${prefix}alt|${meal.key}`,
      limit,
      run: async (chunk) => {
        const proposals = await generateAlternativesForMeals(chunk, { form, directives });
        return (meal) => proposals.find((entry) => entry.slot === meal.slot) || null;
      },
    })
    : new Map();
  const alternativesBySlot = {};
  for (const slot of plan.slots.filter((entry) => entry.missingAlternatives > 0)) {
    const proposal = proposalsByKey.get(mealKey(slot.slot, slot.primaryTitle));
    const seen = new Set(slot.existingTitles.map(normalizedTitle));
    const usedKeys = new Set([...slot.existingTitles.map((title) => mealKey(slot.slot, title)), ...(slot.takenKeys || [])]);
    const added = [];
    for (const alternative of proposal?.alternatives || []) {
      const title = String(alternative?.title || "").replace(/\s+/g, " ").trim().slice(0, 180);
      if (title.length < 3 || seen.has(normalizedTitle(title)) || usedKeys.has(mealKey(slot.slot, title))) continue;
      const hint = cleanHint(alternative.hint);
      assertNoDirectIdentifier(title, identity, `${slot.label} : le titre d'une alternative`);
      assertNoDirectIdentifier(hint, identity, `${slot.label} : la description d'une alternative`);
      seen.add(normalizedTitle(title));
      usedKeys.add(mealKey(slot.slot, title));
      added.push({ title, hint });
      if (added.length === slot.missingAlternatives) break;
    }
    if (added.length < slot.missingAlternatives) {
      throw new Error(`${slot.label} : pas assez d’alternatives nouvelles proposées. Relance le lot Menus et recettes.`);
    }
    alternativesBySlot[slot.slot] = added;
  }
  const recipeItems = plan.slots.flatMap((slot) => [
    ...slot.missingRecipes,
    ...(alternativesBySlot[slot.slot] || []).map((alternative) => ({
      key: mealKey(slot.slot, alternative.title),
      slot: slot.slot,
      slot_label: slot.label,
      title: alternative.title,
      ...(alternative.hint ? { hint: alternative.hint } : {}),
    })),
  ]);
  const recipesByKey = recipeItems.length > 0
    ? await runCachedBatches({
      items: recipeItems,
      size: RECIPES_CHUNK,
      cacheKeyOf: (meal) => `${prefix}recipe|${meal.key}`,
      limit,
      run: async (chunk) => {
        const generated = await generateRecipesForMeals(chunk, { form, directives });
        return (meal) => generated[meal.key] || null;
      },
    })
    : new Map();
  const recipes = {};
  const checked = [];
  for (const item of recipeItems) {
    const recipe = storedRecipe(recipesByKey.get(item.key), `${item.slot_label} · ${item.title}`);
    recipes[item.key] = recipe;
    const isNew = (alternativesBySlot[item.slot] || []).some((alternative) => mealKey(item.slot, alternative.title) === item.key);
    // Contenu généré seulement : titre et description des nouvelles
    // alternatives, recette de tout choix complété.
    checked.push({
      id: item.key,
      label: isNew ? `${item.slot_label}, nouvelle alternative « ${item.title} »` : `${item.slot_label}, recette proposée pour « ${item.title} »`,
      title: isNew ? item.title : "",
      hint: isNew ? item.hint || "" : "",
      recipe,
    });
  }
  const [conflict] = findMenuFoodConflicts(checked, context.packageContext.safetySnapshot);
  if (conflict) throw new Error(conflict.message);
  assertNoAnonymizedMark({ alternativesBySlot, recipes }, "Menus et recettes");
  assertNoAiMention({ alternativesBySlot, recipes }, "Menus et recettes");
  return { alternativesBySlot, recipes, completedSlots: plan.slots.length };
}

/**
 * Ajoute les alternatives et les recettes manquantes dans « Semaine, menus &
 * recettes » sans toucher à rien d'existant (titres, recettes, consignes,
 * validations, confirmations). Un repas complété change de contenu : il
 * redevient « à valider », règle normale de l'éditeur. L'éditeur devient la
 * source des menus du dossier.
 */
export function applyLot2Completion({ client, consultation, workspace, completion }) {
  let current = consultation;
  for (const [slot, alternatives] of Object.entries(completion?.alternativesBySlot || {})) {
    for (const alternative of alternatives) {
      const result = addAlternative({ client, consultation: current }, slot, alternative.title, alternative.hint);
      if (!result.ok) throw new Error(result.error);
      current = result.consultation;
    }
  }
  const mealRecipes = { ...(current.meal_recipes || {}) };
  for (const [key, recipe] of Object.entries(completion?.recipes || {})) {
    // Jamais par-dessus une recette existante (même une simple consigne),
    // y compris apparue entre-temps.
    if (recipePresent(mealRecipes, key)) continue;
    mealRecipes[key] = recipe;
  }
  const next = { ...current, meal_recipes: mealRecipes };
  const saved = markWeekMenusSaved({ client, previous: consultation, next }).consultation;
  return markClientAppWorkspaceWeekEditorSave({
    ...saved,
    editorial_overrides: { ...saved.editorial_overrides, client_app_workspace: workspace },
  });
}

// ─── Lots 3 et 4 : sans IA ──────────────────────────────────────────────

export function buildLot3RawText(context, workspace, settings = LOT_3_DEFAULTS) {
  return envelopeText(context, workspace, "lot_3", {
    rotation: { mode: "four_distinct_weeks" },
    meal_prep: {
      people: settings.people,
      session_count: settings.session_count,
      preferred_days: [...settings.preferred_days],
    },
  });
}

/** Frigo et compléments pré-remplis exactement comme le cadre de préparation. */
const LOT_4_LIST_MAX = 80;

function fridgeLabels(entries, { nested = false } = {}) {
  const labels = (Array.isArray(entries) ? entries : []).flatMap((entry) => (nested
    ? (Array.isArray(entry?.items) ? entry.items : []).map((item) => item?.label)
    : [entry?.label]));
  return labels
    .map((label) => (typeof label === "string" ? label.replace(/\s+/g, " ").trim() : ""))
    .filter((label) => label && label.length <= 180);
}

/**
 * Frigo pré-rempli avec les seuls repères de la fiche frigo du programme,
 * classés comme le programme les classe (« À privilégier » / « À limiter »).
 * Aucun ingrédient des menus n'est ajouté ni classé par le cockpit : c'est
 * un choix d'Anissa (voir docs/DOSSIER-APP-AUTO.md). Plafonné à la limite
 * du contrôle (80 par liste), dans l'ordre du programme.
 */
export function buildLot4PrefillContent(context) {
  const fridge = context?.sourcePlan?.sections?.fridge_data || {};
  const unique = (labels, excluded = new Set()) => {
    const seen = new Set(excluded);
    return labels.filter((label) => {
      const key = label.toLocaleLowerCase("fr");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, LOT_4_LIST_MAX);
  };
  const favorLabels = [...fridgeLabels(fridge.essentials), ...fridgeLabels(fridge.favorite, { nested: true })];
  const limitKeys = new Set(fridgeLabels(fridge.limit, { nested: true }).map((label) => label.toLocaleLowerCase("fr")));
  // Classé des deux côtés par le programme : jamais tranché par le cockpit.
  const both = favorLabels.find((label) => limitKeys.has(label.toLocaleLowerCase("fr")));
  if (both) {
    throw new Error(`Le frigo du programme classe « ${both} » des deux côtés : à trancher par Anissa. Prépare ce lot à la main.`);
  }
  const toFavor = unique(favorLabels);
  if (toFavor.length === 0) {
    throw new Error("Pas de repères frigo dans le programme : fais ce lot à la main.");
  }
  const toLimit = unique(fridgeLabels(fridge.limit, { nested: true }), new Set(toFavor.map((label) => label.toLocaleLowerCase("fr"))));
  const template = buildClientAppWorkspaceLotTemplate(context, "lot_4", null).content;
  return { fridge: { to_favor: toFavor, to_limit: toLimit }, supplements: template.supplements };
}

export function buildLot4RawText(context, workspace) {
  return envelopeText(context, workspace, "lot_4", buildLot4PrefillContent(context));
}

// ─── Orchestration ──────────────────────────────────────────────────────

function lotErrorMessage(error) {
  const message = error?.message || String(error || "");
  if (error?.payload?.aborted) return "La préparation a été interrompue. Relance ce lot.";
  if (error?.status === 429) return "Le service de rédaction est saturé. Réessaie dans une minute.";
  if (error?.status >= 500) return "Le service de rédaction ne répond pas. Réessaie dans un instant.";
  if (error?.payload?.parseError) return "La proposition reçue était illisible. Relance ce lot.";
  return message || "La préparation de ce lot a échoué. Relance-le.";
}

/**
 * Prépare les lots demandés (1 et/ou 2) en parallèle, puis les ajoute au
 * dossier l'un après l'autre. Un lot en échec n'empêche pas l'autre.
 * @returns {Promise<{ consultation: object, results: Record<string, { ok: boolean, error?: string }> }>}
 */
function stableJson(value) {
  const stable = (item) => {
    if (Array.isArray(item)) return item.map(stable);
    if (!item || typeof item !== "object") return item;
    return Object.fromEntries(Object.keys(item).sort().filter((key) => item[key] !== undefined).map((key) => [key, stable(item[key])]));
  };
  return JSON.stringify(stable(value ?? null));
}

export const MESSAGE_AUTO_STALE = "La fiche a changé entre-temps : recharge la page puis relance. Rien n’a été enregistré.";
export const MESSAGE_AUTO_UNSAVED = "Enregistre d’abord tes modifications, puis relance. Rien n’a été enregistré.";
export const MESSAGE_AUTO_CLOUD_DISABLED = "La base en ligne n’est pas disponible : la préparation automatique ne peut rien enregistrer. Réessaie quand la connexion est rétablie.";
export const MESSAGE_AUTO_UNREADABLE = "Impossible de relire la fiche avant l’enregistrement : rien n’a été enregistré. Réessaie.";

/**
 * Juste avant l'enregistrement : la fiche et la consultation sont relues (copie
 * locale) et
 * toutes les portes rejouées. Le moindre changement (profil, programme,
 * menus de l'éditeur Semaine, dossier enregistré ailleurs, statut MGD)
 * annule l'enregistrement. Sans relecture possible : rien n'est écrit.
 *
 * Écriture concurrente depuis un autre appareil : couverte côté serveur. Un
 * programme externe ne s'enregistre que par la RPC compare-and-set
 * sauvegarder_plan_externe_cas_atomiquement, avec le request_id attendu
 * (saveEditorialConsultationWithFreshExternalRequest → p_expected_request_id).
 * Si la ligne a changé en base depuis la version lue, la sauvegarde est refusée.
 */
/** Empreinte de la ligne relue, hors dossier application (écrit par la préparation elle-même). */
function rowFingerprint(row) {
  const overrides = { ...(row?.editorial_overrides || {}) };
  delete overrides.client_app_workspace;
  return sha256HexSync(stableJson({ ...row, editorial_overrides: overrides }));
}

async function assertUnchangedBeforeSave({ client, consultation, context, readFresh, verifierMgd, expectedRowFingerprint = null }) {
  let fresh = null;
  try {
    fresh = typeof readFresh === "function" ? await readFresh() : null;
  } catch {
    fresh = null;
  }
  if (fresh?.cloudDisabled) throw new Error(MESSAGE_AUTO_CLOUD_DISABLED);
  if (!fresh?.client || !fresh?.consultation) throw new Error(MESSAGE_AUTO_UNREADABLE);
  // Programme modifié à l'écran mais pas encore enregistré (ex. ouvert depuis
  // l'éditeur du plan) : le dire, plutôt que « la fiche a changé ».
  const programmeText = (value) => stableJson({
    plan: String(value?.nutrition_plan || value?.nutritionPlan || ""),
    supplements: value?.supplements ?? null,
  });
  if (programmeText(fresh.consultation) !== programmeText(consultation)) {
    throw new Error(MESSAGE_AUTO_UNSAVED);
  }
  const storedRevision = (value) => value?.editorial_overrides?.client_app_workspace?.draft_revision ?? null;
  let freshRevision = null;
  try {
    freshRevision = buildClientAppWorkspaceContext({
      client: fresh.client,
      consultation: fresh.consultation,
      directives: context.directives || "",
    }).sourceRevision;
  } catch {
    freshRevision = null;
  }
  // Plan du cockpit (mode standard) : la RPC
  // sauvegarder_consultation_nutritionnelle_initiale n'a PAS de
  // compare-and-set, et la révision serveur n'est pas incrémentée pour un
  // plan initial. On compare donc l'empreinte de toute la ligne relue (hors
  // dossier application) entre le début de la préparation et l'écriture :
  // un enregistrement fait ailleurs entre-temps change l'empreinte.
  // Mode externe : en plus, la RPC compare-and-set sur request_id.
  if (expectedRowFingerprint && rowFingerprint(fresh.consultation) !== expectedRowFingerprint) {
    throw new Error(MESSAGE_AUTO_STALE);
  }
  if (stableJson(fresh.client.form) !== stableJson(client?.form)
      || weekEditorContentSha256(fresh.consultation) !== weekEditorContentSha256(consultation)
      || storedRevision(fresh.consultation) !== storedRevision(consultation)
      || freshRevision !== context.sourceRevision) {
    throw new Error(MESSAGE_AUTO_STALE);
  }
  const mgdState = await readDossierAutoMgdState(fresh.client.id, verifierMgd ? { verifierMgd } : undefined);
  const blocker = getDossierAutoGenerationBlocker({ client: fresh.client, consultation: fresh.consultation, mgdState });
  if (blocker) throw new Error(blocker);
  return rowFingerprint(fresh.consultation);
}

/**
 * Relecture en base (même source que l'écran) : ligne clients et ligne
 * nutrition_consultations, normalisées comme à l'enregistrement confirmé
 * (normalizeNutritionConsultationCloudRow puis l'entrée du store).
 * @returns {Promise<{ client: object, consultation: object }|null>} null si illisible
 */
export async function readDossierAutoFreshFromCloud({ client, consultation, db, buildEntry }) {
  if (!db?.from || typeof buildEntry !== "function" || !client?.id || !consultation?.id) return null;
  const [clientRes, consultationRes] = await Promise.all([
    db.from("clients").select("*").eq("id", client.id).maybeSingle(),
    db.from("nutrition_consultations").select("*").eq("id", consultation.id).maybeSingle(),
  ]);
  if (clientRes?.error || consultationRes?.error || !clientRes?.data || !consultationRes?.data
      || clientRes.data.is_deleted || consultationRes.data.is_deleted) {
    return null;
  }
  const normalized = normalizeNutritionConsultationCloudRow(consultationRes.data, client.id);
  if (!normalized || normalized.id !== consultation.id) return null;
  return {
    client: { ...client, id: clientRes.data.id, form: clientRes.data.form || {} },
    consultation: buildEntry(normalized),
  };
}

export async function prepareClientAppDossierAuto({ client, consultation, context, workspace, lots = AUTO_LOT_IDS, onProgress = () => {}, verifierMgd, readFresh, signal = null }) {
  const limit = createLimiter(AI_CONCURRENCY, signal);
  const mgdState = await readDossierAutoMgdState(client?.id, verifierMgd ? { verifierMgd } : undefined);
  const blocker = getDossierAutoGenerationBlocker({ client, consultation, mgdState });
  if (blocker) throw new Error(blocker);
  const startRowFingerprint = await assertUnchangedBeforeSave({ client, consultation, context, readFresh, verifierMgd });
  const wanted = AUTO_LOT_IDS.filter((lotId) => lots.includes(lotId));
  const results = {};
  const lot2Plan = wanted.includes("lot_2") ? planLot2Completion({ client, consultation, workspace, context }) : null;
  const lot2Blocker = wanted.includes("lot_2") ? getLot2AutoGenerationBlocker({ client, consultation, workspace, context }) : null;
  if (lot2Blocker) results.lot_2 = { ok: false, skipped: true, error: lot2Blocker };
  const run = async (lotId, task) => {
    onProgress(lotId, "running");
    try {
      const value = await task();
      onProgress(lotId, "generated");
      return { ok: true, value };
    } catch (error) {
      onProgress(lotId, "error");
      return { ok: false, error: lotErrorMessage(error) };
    }
  };
  const [lot1, lot2] = await Promise.all([
    wanted.includes("lot_1") ? run("lot_1", () => limit(() => generateClientAppLot1({ client, context, workspace, signal }))) : null,
    wanted.includes("lot_2") && !lot2Blocker ? run("lot_2", () => (lot2Plan.mode === "full"
      ? generateClientAppLot2({ client, context, workspace, signal, limit })
      : generateLot2Completion({ client, context, plan: lot2Plan, signal, limit }))) : null,
  ]);
  if (signal?.aborted) throw abortedError();
  if (lot1?.ok || lot2?.ok) {
    await assertUnchangedBeforeSave({ client, consultation, context, readFresh, verifierMgd, expectedRowFingerprint: startRowFingerprint });
  }
  let next = consultation;
  let nextWorkspace = workspace;
  if (lot1) {
    if (lot1.ok) {
      try {
        nextWorkspace = importClientAppWorkspaceLot({ workspace: nextWorkspace, context, lotId: "lot_1", rawText: lot1.value.rawText });
        // Points à vérifier gardés avec le lot : visibles après rechargement.
        if (lot1.value.warnings.length > 0) {
          nextWorkspace = {
            ...nextWorkspace,
            lots: { ...nextWorkspace.lots, lot_1: { ...nextWorkspace.lots.lot_1, review_notes: [...lot1.value.warnings] } },
          };
        }
        next = { ...next, editorial_overrides: { ...(next.editorial_overrides || {}), client_app_workspace: nextWorkspace } };
        results.lot_1 = lot1.value.warnings.length > 0 ? { ok: true, warnings: lot1.value.warnings } : { ok: true };
      } catch (error) {
        results.lot_1 = { ok: false, error: lotErrorMessage(error) };
      }
    } else {
      results.lot_1 = { ok: false, error: lot1.error };
    }
  }
  if (lot2) {
    if (lot2.ok) {
      try {
        // Nouveaux menus = lots 3 et 4 à reconfirmer (retirés par l'import,
        // comme pour un lot collé) : dit explicitement, jamais en silence.
        if (lot2Plan.mode === "full") {
          const revoked = ["lot_3", "lot_4"].filter((lotId) => nextWorkspace?.lots?.[lotId]);
          next = applyGeneratedLot2({ client, consultation: next, context, workspace: nextWorkspace, ...lot2.value });
          nextWorkspace = next.editorial_overrides.client_app_workspace;
          results.lot_2 = revoked.length > 0 ? { ok: true, revoked } : { ok: true };
        } else {
          next = applyLot2Completion({ client, consultation: next, workspace: nextWorkspace, completion: lot2.value });
          nextWorkspace = next.editorial_overrides.client_app_workspace;
          const warnings = [
            ...lot2Plan.skipped,
            ...(lot2Plan.incompleteRecipes > 0 ? [incompleteRecipesMessage(lot2Plan.incompleteRecipes)] : []),
          ];
          results.lot_2 = {
            ok: true,
            mode: "complete",
            completedSlots: lot2.value.completedSlots,
            ...(warnings.length > 0 ? { warnings } : {}),
          };
        }
      } catch (error) {
        results.lot_2 = { ok: false, error: lotErrorMessage(error) };
      }
    } else {
      results.lot_2 = { ok: false, error: lot2.error };
    }
  }
  return {
    consultation: { ...next, editorial_overrides: { ...(next.editorial_overrides || {}), client_app_workspace: nextWorkspace } },
    workspace: nextWorkspace,
    results,
  };
}

// ─── Une seule préparation à la fois par consultation ──────────────────
//
// L'état vit hors du composant : fermer puis rouvrir le Dossier application
// ne relance pas une seconde préparation par-dessus la première.
const runsInFlight = new Map();

export const MESSAGE_AUTO_ALREADY_RUNNING = "Une préparation est déjà en cours pour ce programme. Attends qu’elle se termine.";

export function isDossierAutoRunning(consultationId) {
  return runsInFlight.has(String(consultationId || ""));
}

/** Lance `task(signal)` si aucune préparation n'est en cours pour cette consultation. */
export function startDossierAutoRun(consultationId, task) {
  const key = String(consultationId || "");
  if (!key) return Promise.reject(new Error("Consultation indisponible."));
  if (runsInFlight.has(key)) return Promise.reject(new Error(MESSAGE_AUTO_ALREADY_RUNNING));
  const controller = new AbortController();
  const promise = Promise.resolve()
    .then(() => task(controller.signal))
    .finally(() => { runsInFlight.delete(key); });
  runsInFlight.set(key, { controller, promise });
  return promise;
}

/** Interrompt la préparation en cours (aucun nouvel appel, rien n'est écrit). */
export function abortDossierAutoRun(consultationId) {
  runsInFlight.get(String(consultationId || ""))?.controller.abort();
}
